import { ReactNode } from 'react';

import { FormattedMessage } from 'dibs-react-intl';

// http://www.w3.org/TR/html5/forms.html%23valid-e-mail-address
const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const specialChars = /^[^!@#$%^&*()+=><?]*$/;

export function validateRegex(
    field: string | null | undefined,
    regex: RegExp,
    noMatchMsg: ReactNode
): ReactNode {
    if (!field) {
        // don't test empty string, that will be checked by `validateRequiredField`
        return null;
    }

    const isMatch = field.match(regex);

    if (isMatch) {
        return null;
    }

    return noMatchMsg;
}

function hasValue(field: unknown): boolean {
    // false is a valid selected value, it's not empty.
    if (!field && field !== false && field !== 0) {
        return false;
    }

    return true;
}

export function validateRequiredField(field: unknown): ReactNode {
    const fieldHasValue = hasValue(field);

    if (fieldHasValue) {
        return null;
    }

    return (
        <FormattedMessage
            id="common.helpers.formValidationHelpers.required"
            defaultMessage="This field is required"
        />
    );
}

export function validateNoSpecialCharacters(field?: string | null): ReactNode {
    return validateRegex(
        field,
        specialChars,
        <FormattedMessage
            id="common.helpers.formValidationHelpers.noSpecial"
            defaultMessage="No special characters"
        />
    );
}

export function validateEmail(field?: string | null): ReactNode {
    return validateRegex(
        field,
        emailRegex,
        <FormattedMessage
            id="common.helpers.formValidationHelpers.email"
            defaultMessage="Please enter a valid email address"
        />
    );
}

export function isFieldTrue(field?: unknown): boolean {
    if (typeof field !== 'boolean' || !field) {
        return false;
    }

    return true;
}

function isStringTooLong(maxLength: number, field?: string): boolean {
    if (!field || field.length <= maxLength) {
        return false;
    }

    return true;
}

export const validateFieldLength =
    (maxLength: number) =>
    (field?: string): ReactNode => {
        const isTooLong = isStringTooLong(maxLength, field);

        if (isTooLong) {
            return (
                <FormattedMessage
                    id="common.helpers.formValidationHelpers.fieldLength"
                    defaultMessage="Max field length is {numChars} characters"
                    values={{
                        numChars: maxLength,
                    }}
                />
            );
        }

        return null;
    };
